import React from 'react'
import FlightIcon from '@mui/icons-material/Flight'
import HotelIcon from '@mui/icons-material/Hotel'
import isAeDomain from '@/src/utils/generalUtils/isAeDomain'

// ============================================================================
// Types & Interfaces
// ============================================================================

/** User menu item interface */
export interface UserMenuItem {
    label: string
    href: string
}

/** Navigation link interface */
export interface NavLink {
    label: string
    href: string
    labelKey?: string
    icon?: React.ReactNode
    isActive?: boolean
}

/** Header visibility flags interface */
export interface HeaderVisibility {
    showLangCurrDropdown: boolean
    showDeals: boolean
    shouldHideSignIn: boolean
    isOffersPage: boolean
    isItineraryPage: boolean
    isSignInPage: boolean
}

/** User object interface for display name extraction */
export interface User {
    firstName?: string
    displayName?: string
    email?: string
}

// ============================================================================
// Support URL Utilities
// ============================================================================

/** Gets the support contact URL based on domain and locale */
export const getSupportHref = (locale: string, isMounted: boolean): string => {
    if (!isMounted) {
        return `/contactUs.${locale}.html`
    }

    const isAe = isAeDomain()
    return isAe ? `/contact.${locale}.html` : `/contactUs.${locale}.html`
}

// ============================================================================
// User Menu Utilities
// ============================================================================

/** Generates user menu items for header */
export const getUserMenuItems = (
    t: (key: string) => string,
    locale: string
): UserMenuItem[] => {
    return [
        { label: t('userMenu.myBookings'), href: '/accounts/trips' },
        { label: t('userMenu.myWallet'), href: `/${locale}/wallet` },
        { label: t('userMenu.myReviews'), href: '/manageReviews' },
        { label: t('userMenu.giveFeedback'), href: '/feedback' }
    ]
}

/** Gets user display name with fallback logic (firstName → displayName → email username → 'User') */
export const getUserDisplayName = (user: User): string => {
    if (user.firstName) {
        return user.firstName
    }
    
    if (user.displayName) {
        return user.displayName
    }
    
    if (user.email) {
        return user.email.split('@')[0]
    }
    
    return 'User'
}

// ============================================================================
// Navigation Utilities
// ============================================================================

/** Generates navigation links for Flyin header variant */
export const getFlyinNavLinks = (locale: string): NavLink[] => {
    const localePrefix = `/${locale}`
    
    return [
        { label: 'Flights', href: `${localePrefix}/flights` },
        { label: 'Hotels', href: `${localePrefix}/hotels` },
        { label: 'fph', href: `${localePrefix}/flights-hotels` }
    ]
}

/** Generates navigation links for Cleartrip header variant with active state */
export const getCTNavLinks = (
    locale: string,
    activeVertical: string | null
): NavLink[] => {
    const localePrefix = `/${locale}`
    
    return [
        { 
            label: 'Flights', 
            href: `${localePrefix}/flights`, 
            isActive: activeVertical === 'flights' 
        },
        { 
            label: 'Hotels', 
            href: `${localePrefix}/hotels`, 
            isActive: activeVertical === 'hotels' 
        }
    ]
}

/** Gets icon component for navigation item label (case-insensitive) */
export const getNavIcon = (label: string): React.ReactNode | null => {
    const lowerLabel = label.toLowerCase()
    if (lowerLabel === 'flights') {
        return React.createElement(FlightIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    if (lowerLabel === 'hotels') {
        return React.createElement(HotelIcon, { sx: { fontSize: 20, color: 'inherit' } })
    }
    return null
}

/** Transforms label to translation key for Flyin header navigation links */
export const getTranslationKey = (label: string, labelKey?: string): string => {
    if (labelKey) {
        return labelKey
    }
    
    return label
        .replace(/ \+ /g, 'Plus')
        .replace(/ /g, '')
        .replace(/^[A-Z]/, c => c.toLowerCase())
}

// ============================================================================
// Visibility Utilities
// ============================================================================

/** Determines visibility flags for header elements based on current pathname and variant */
export const getHeaderVisibility = (
    pathname: string,
    variant: 'flyin' | 'cleartrip' = 'flyin'
): HeaderVisibility => {
    const isHomepage = pathname === '/' || /^\/[a-z]{2}$/.test(pathname)
    const isFlightsPage = pathname === '/flights' || /^\/[a-z]{2}\/flights\/?$/.test(pathname)
    const isSRP = pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)
    const isOffersPage = pathname.includes('/offers')
    const isItineraryPage = pathname.includes('/flights/itinerary')
    const isSignInPage = pathname.includes('/signin')
    
    const showLangCurrDropdown =
        variant === 'flyin'
            ? isHomepage || isFlightsPage || isSRP || isOffersPage
            : isHomepage || isSRP
    
    const showDeals = variant === 'flyin' ? isHomepage || isFlightsPage || isSRP : isHomepage || isSRP
    const shouldHideSignIn = isItineraryPage || isSignInPage || isOffersPage
    
    return {
        showLangCurrDropdown,
        showDeals,
        shouldHideSignIn,
        isOffersPage,
        isItineraryPage,
        isSignInPage
    }
}
